/* eslint-disable react-hooks/exhaustive-deps */

import { IGResponse } from "@common/models";
import { ICity } from "@common/models/CMS";
import { fitToPolygon, hideGoogleMarkerInfoWindow } from "@components/general/GoogleMap";
import { IViewport } from "@config/appConfig";
import { IAppState } from "@misc/State";
import Config from "@service/Config";
import { useApolloQuery } from "@service/graphql";
import { MAP_CITIES } from "@service/graphql/query/Query.Map";
import Translate from "@service/Translate";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import CardItem from "../CardItem";
import CardWidgetController from "../CardWidgetController";
import CitySectionItem from "./CitySectionItem";
import { MapWidgetAction, MapWidgetContext } from "./index";

export enum CitySectionStep { CityContent, CityItems }

export const CitySection = () => {
    const { tr } = Translate;
    const { data } = useApolloQuery<IGResponse<ICity[]>>(MAP_CITIES);
    const mapMarkerTimer = useRef<null | NodeJS.Timeout>(null);
    const cityItemsRefs = useRef<HTMLLIElement[]>([]);
    const cityItemsItemsRef = useRef<HTMLUListElement>(null);
    const currentIndex = useRef(-1);
    const [activeItemIndex, setActiveItemIndex] = useState<number>(currentIndex.current);
    const [currentCityId, setCurrentCityId] = useState<string>();
    const [maxHeight, setMaxHeight] = useState<number>(85);
    const { state: { dataSource, currentMarker, currentCity, polys, mapMarkers }, dispatch } = useContext(MapWidgetContext);
    const touchStartCityItem = useRef<number>(0);
    const touchStartCityExpand = useRef<number>(0);
    const cities: ICity[] = data?.city?.filter(x => x.tourismOrder).slice().sort((a, b) => (a.tourismOrder as number) - (b.tourismOrder as number)) || [];
    const { width } = useSelector((state: IAppState) => ({ width: state.windowWidth }));
    const { medium } = Config.get<IViewport>('viewport');

    useEffect(() => {
        return (() => {
            document.getElementById('header')?.style.removeProperty('background-color');
        })
    }, [])

    useEffect(() => {
        if (width <= medium)
            if (activeItemIndex !== -1 && cityItemsRefs.current && cityItemsRefs.current[activeItemIndex])
                cityItemsItemsRef.current?.scrollTo({ left: cityItemsRefs.current[activeItemIndex].offsetLeft - 75, behavior: "smooth" });
            else
                cityItemsItemsRef.current?.scrollTo({ left: 0 })
    }, [activeItemIndex, currentCity])

    useEffect(() => {
        if (width <= medium && activeItemIndex !== -1 && cities.length) {
            onCityMouseEnter(cities[activeItemIndex]);
            setCurrentCityId(cities[activeItemIndex].contentItemId);
        }
        else {
            setCurrentCityId("");
        }
    }, [activeItemIndex])

    const removePolyFromMap = (id: string) => {
        polys.splice(polys.findIndex(x => x.contentItemId === id));
        dispatch({ type: MapWidgetAction.Polys, payload: { polys: polys.slice() } });
    }

    const onCityMouseEnter = (city: ICity) => {
        clearInterval(mapMarkerTimer.current as NodeJS.Timeout);
        polys.push({
            contentItemId: city.contentItemId,
            points: city.borderCoordinates,
            fill: "#57C5C6",
            fillOpacity: 0.6,
            stroke: "#fafafa",
            strokeWidth: 2
        });
        dispatch({ type: MapWidgetAction.Polys, payload: { polys: polys.slice() } });
        dispatch({
            type: MapWidgetAction.MapMakers, payload: {
                mapMarkers:
                    mapMarkers.map(mapMarker => {
                        if (!dataSource.filter(item => item?.city?.contentItems.some(x => x.contentItemId === city?.contentItemId))
                            .some(x => x.localization?.localizationSet === mapMarker.id))
                            return { ...mapMarker, opacity: 0.2 }
                        else
                            return { ...mapMarker, opacity: 1 }
                    })
            }
        });
    }

    const onTouchStartCityItem = (event: any) => {
        if (event.touches)
            touchStartCityItem.current = event.touches[0].clientX;
    }

    const onTouchEndCityItem = (event: any) => {
        const touchEndCityItem = (event.changedTouches && event.changedTouches[0].clientX) || 0;
        if (touchEndCityItem - touchStartCityItem.current > 30 && currentIndex.current !== 0) {
            //Prev
            currentIndex.current--;
            cities.length && onCityMouseLeave(cities[activeItemIndex]);
        }
        else if (touchStartCityItem.current - touchEndCityItem > 30 && currentIndex.current !== (cities.length || 0) - 1) {
            //next
            currentIndex.current++;
            activeItemIndex !== -1 && cities.length && onCityMouseLeave(cities[activeItemIndex]);
        }
        setActiveItemIndex(currentIndex.current);
        hideGoogleMarkerInfoWindow(currentMarker?.contentItemId || "");
        dispatch({ type: MapWidgetAction.CurrentMarker, payload: { currentMarker: {} } });
    }

    const onTouchStartCityExpand = (event: any) => {
        if (event.touches)
            touchStartCityExpand.current = event.touches[0].clientY;
    }

    const onTouchMoveCityExpand = (event: any) => {
        const touchMoveCityExpand: number = (event.changedTouches && event.changedTouches[0].clientY) || 0;
        let currentMaxHeight = maxHeight;
        if (touchMoveCityExpand < touchStartCityExpand.current) {
            currentMaxHeight = maxHeight + (touchStartCityExpand.current - touchMoveCityExpand);
            setMaxHeight(currentMaxHeight <= 260 ? currentMaxHeight : 260);
        }
        else {
            currentMaxHeight = maxHeight - (touchMoveCityExpand - touchStartCityExpand.current);
            setMaxHeight(currentMaxHeight >= 85 ? currentMaxHeight : 85);
        }
    }

    const onTouchEndCityExpand = (event: any) => {
        const touchEndCityExpand = (event.changedTouches && event.changedTouches[0].clientY) || 0;
        if (Math.abs(touchEndCityExpand - touchStartCityExpand.current) > 30)
            if (touchEndCityExpand < touchStartCityExpand.current) {
                currentIndex.current = 0;
                setActiveItemIndex(0);
            }
            else if (currentCityId) {
                onShowAllTopDestinations();
            }
    }

    const onCityMouseLeave = (city: ICity) => {
        if (!currentCity?.contentItemId && polys.some(x => x.contentItemId === city.contentItemId)) {
            polys.splice(polys.findIndex(x => x.contentItemId === city.contentItemId));
            dispatch({ type: MapWidgetAction.Polys, payload: { polys: polys.slice() } });
            mapMarkerTimer.current = setInterval(() => {
                dispatch({
                    type: MapWidgetAction.MapMakers, payload: {
                        mapMarkers:
                            mapMarkers.map(mapMarker => ({ ...mapMarker, opacity: 1 }))
                    }
                });
                clearInterval(mapMarkerTimer.current as NodeJS.Timeout);
            }, 150)
        }
    }

    const onCityBackwardBtnClick = (city: ICity) => {
        document.getElementById('header')?.style.removeProperty('background-color');
        dispatch({ type: MapWidgetAction.CurrentCity, payload: { currentCity: {} } });
        if (width > medium) {
            removePolyFromMap(city.contentItemId || "");
            dispatch({
                type: MapWidgetAction.MapMakers, payload: {
                    mapMarkers:
                        mapMarkers.map(mapMarker => ({ ...mapMarker, opacity: 1 }))
                }
            });
        }
        fitToPolygon("40m56vzf9262n68c667ebctgcw");
    }

    const onShowAllTopDestinations = () => {
        removePolyFromMap(currentCityId || "");
        dispatch({ type: MapWidgetAction.CurrentCity, payload: { currentCity: {} } });
        currentIndex.current = -1;
        setActiveItemIndex(-1);
        dispatch({
            type: MapWidgetAction.MapMakers, payload: {
                mapMarkers:
                    mapMarkers.map(mapMarker => ({ ...mapMarker, opacity: 1 }))
            }
        });
    }

    const onCityClick = (city: ICity) => {
        currentIndex.current = cities.findIndex(x => x.contentItemId === city.contentItemId);
        setActiveItemIndex(currentIndex.current);
        hideGoogleMarkerInfoWindow(currentMarker?.contentItemId || "");
        document.getElementById('header')?.style?.setProperty('background-color', "#ffffff");
        dispatch({ type: MapWidgetAction.CurrentCity, payload: { currentCity: city } });
        fitToPolygon(city?.contentItemId || "");
    }

    return (
        <div
            style={{ maxHeight: width > medium ? "" : maxHeight }}
            className={`city-section ${currentCity?.contentItemId ? "current-city" : ""}`}
        >
            {
                currentCity?.contentItemId ?
                    <CitySectionItem onBackwardBtnClick={onCityBackwardBtnClick} />
                    :
                    <Fragment>
                        <div
                            onTouchStart={onTouchStartCityExpand}
                            onTouchMove={onTouchMoveCityExpand}
                            onTouchEnd={onTouchEndCityExpand}
                            className="header-section"
                        >
                            <div className="slider-border" />
                            <h3 className="city-items-title">{tr("iran_cities")}</h3>
                        </div>
                        <ul
                            ref={cityItemsItemsRef}
                            className="row flex city-items"
                            onTouchStart={onTouchStartCityItem}
                            onTouchEnd={onTouchEndCityItem}
                        >
                            {
                                cities.map((city, index) =>
                                    <li
                                        key={`city-item-${index}`}
                                        ref={ref => cityItemsRefs.current[index] = ref as HTMLLIElement}
                                        className={`col-1-1 col-md-1-auto city-item ${activeItemIndex === index ? "active"
                                            : activeItemIndex !== -1 ? "hide" : ""}`}
                                        onMouseEnter={() => onCityMouseEnter(city)}
                                        onMouseLeave={() => onCityMouseLeave(city)}
                                        onClick={() => onCityClick(city)}
                                    >
                                        <CardItem
                                            card={{
                                                displayText: city.name,
                                                image: city.cover,
                                                forecaWeatherId: city.forecaWeatherId
                                            }}
                                            cardWidgetController={new CardWidgetController({ style: 'style4' })}
                                        />
                                    </li>
                                )
                            }
                        </ul>
                    </Fragment>
            }
        </div>
    )
}

export default CitySection;